import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useHttpApiCall from '../../services/useHttpApiCall';
import { closePanel, setExpandedId } from '../../redux/aiInsights/ai.actions';
import InsightItem from './InsightItem';
import ThumbnailPreview from './ThumbnailPreview';
import betaTag from '../../assets/images/beta.png';

export default function AiInsightsPanel({ openAiSlider, openAiScreen }) {
  const [listPayload, setListPayload] = useState(undefined);
  const [detailPayload, setDetailPayload] = useState(undefined);
  const [viewPayload, setViewPayload] = useState(null);

  const [expandedDetails, setExpandedDetails] = useState({});

  const [viewRes, viewError] = useHttpApiCall(viewPayload);
  const [listData, listLoading, listError] = useHttpApiCall(listPayload);
  const [detailData, detailLoading, detailError] = useHttpApiCall(detailPayload);

  const dispatch = useDispatch();

  const { expandedId } = useSelector((state) => state.aiInsights);
  const { previewImages, activePreviewId } = useSelector((state) => state.aiInsights);

  console.log('activePreviewId', expandedId);
  const previewImage = previewImages?.[activePreviewId];

  const [insights, setInsights] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Track loading state for specific insight details
  const [loadingDetails, setLoadingDetails] = useState({});

  // Ref for the container element
  const containerRef = useRef(null);

  // Track if we're currently fetching list or detail
  const isFetchingList = useRef(false);

  // Handle panel close from props
  const handleClose = () => {
    if (openAiScreen) {
      openAiScreen('close_slider');
    } else {
      dispatch(closePanel());
    }
  };

  // Clear errors when starting new requests
  useEffect(() => {
    if (listPayload) {
      setErrorMessage(null);
    }
  }, [listPayload]);

  // Clear error when expanding/collapsing
  useEffect(() => {
    if (expandedId) {
      setErrorMessage(null);
    }
  }, [expandedId]);

  // INITIAL INSIGHTS FETCH - only fetch if panel is open
  useEffect(() => {
    if (openAiSlider?.ai_screen) {
      isFetchingList.current = true;
      setListPayload({
        method: 'GET',
        auth: true,
        url: 'v0/insights/'
      });
      setIsInitialLoad(true);

      // Reset states when panel opens
      setInsights([]);
      setExpandedDetails({});
      setErrorMessage(null);
    } else {
      // Clear data when panel closes
      setInsights([]);
      setExpandedDetails({});
      setNextUrl(null);
      setHasMore(true);
      setErrorMessage(null);
      setExpandedId(null);
    }
  }, [openAiSlider?.ai_screen]);

  // Handle LIST API response errors
  useEffect(() => {
    if (listError) {
      console.error('Error fetching insights list:', listError);
      setErrorMessage(listError.message || 'Failed to load insights. Please try again.');
      setHasMore(false);
      setIsInitialLoad(false);
      isFetchingList.current = false;
    }
  }, [listError]);

  // Handle LIST API success response (for paginated insights)
  useEffect(() => {
    // Only process successful list responses
    if (listData?.data?.status === 1 && Array.isArray(listData.data.data)) {
      // Handle insights based on whether it's initial load or pagination
      setInsights((prev) => {
        if (isInitialLoad) {
          // Initial load - replace all insights
          return listData.data.data;
        } else {
          // Pagination load - append new insights, filter duplicates
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = listData.data.data.filter((item) => !existingIds.has(item.id));
          return [...prev, ...newItems];
        }
      });

      // Handle the next URL
      if (listData.data.next) {
        let extractedNextUrl = listData.data.next;

        // If it's a full URL starting with http/https
        if (extractedNextUrl.startsWith('http')) {
          const urlObj = new URL(extractedNextUrl);
          // Get the pathname and search params
          const pathAndQuery = urlObj.pathname + urlObj.search;

          // Assuming your API calls go through a base URL that already includes /reports/
          // Remove the leading /reports/ to avoid duplication
          if (pathAndQuery.startsWith('/reports/')) {
            // Remove the /reports prefix
            extractedNextUrl = pathAndQuery.substring('/reports'.length);
          } else {
            extractedNextUrl = pathAndQuery;
          }
        }

        setNextUrl(extractedNextUrl);
        setHasMore(true);
      } else {
        setNextUrl(null);
        setHasMore(false);
      }

      // Clear any previous errors
      setErrorMessage(null);
      setIsInitialLoad(false);
      isFetchingList.current = false;
    } else if (listData?.data?.status === 0) {
      // API returned error status
      setErrorMessage(listData.data.comment || 'Failed to load insights. Please try again.');
      setHasMore(false);
      setIsInitialLoad(false);
      isFetchingList.current = false;
    }
  }, [listData, isInitialLoad]);

  // Handle DETAIL API errors
  useEffect(() => {
    if (detailError) {
      console.error('Error fetching insight detail:', detailError);
      // Show error for expanded item
      if (expandedId) {
        setExpandedDetails((prev) => ({
          ...prev,
          [expandedId]: {
            ...prev[expandedId],
            error: detailError.message || 'Failed to load details'
          }
        }));
        // Clear loading state
        setLoadingDetails((prev) => ({
          ...prev,
          [expandedId]: false
        }));
      }
    }
  }, [detailError, expandedId]);

  // Handle DETAIL API success response (for expanded insight)
  useEffect(() => {
    if (detailData?.data?.status === 1 && detailData.data.data && expandedId) {
      const detail = detailData.data.data;
      const detailId = detail.id || expandedId;

      // Only update if this is for the currently expanded insight
      if (detailId === expandedId) {
        console.log('Setting expanded details for:', detailId);
        setExpandedDetails((prev) => ({
          ...prev,
          [detailId]: detail
        }));

        // Clear loading state
        setLoadingDetails((prev) => ({
          ...prev,
          [detailId]: false
        }));

        // If detail API says it's already viewed, update the insights list
        if (detail.is_already_viewed) {
          setInsights((prev) =>
            prev.map((item) => (item.id === detailId ? { ...item, is_already_viewed: true } : item))
          );
        }
      }
    } else if (detailData?.data?.status === 0 && expandedId) {
      // API returned error status for detail
      setExpandedDetails((prev) => ({
        ...prev,
        [expandedId]: {
          ...prev[expandedId],
          error: detailData.data.comment || 'Failed to load insight details'
        }
      }));
      // Clear loading state
      setLoadingDetails((prev) => ({
        ...prev,
        [expandedId]: false
      }));
    }
  }, [detailData, expandedId]);

  // Handle VIEW API errors
  useEffect(() => {
    if (viewError) {
      console.error('Error marking insight as viewed:', viewError);
      // Silent fail - don't show error to user for this action
    }
  }, [viewError]);

  // Handle successful mark as viewed
  useEffect(() => {
    if (viewRes?.data?.status === 1 && viewRes?.data?.data?.insight_id) {
      const viewedId = viewRes.data.data.insight_id;

      // Update the insights list to mark as viewed locally
      setInsights((prev) =>
        prev.map((item) => (item.id === viewedId ? { ...item, is_already_viewed: true } : item))
      );

      // Also update expandedDetails if it exists
      setExpandedDetails((prev) => {
        if (prev[viewedId]) {
          return {
            ...prev,
            [viewedId]: {
              ...prev[viewedId],
              is_already_viewed: true
            }
          };
        }
        return prev;
      });
    }
  }, [viewRes]);

  // Load more when scrolling
  const handleScroll = useCallback(() => {
    if (
      !containerRef.current ||
      listLoading ||
      isFetchingList.current ||
      !hasMore ||
      expandedId ||
      errorMessage
    )
      return;

    const container = containerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Check if we're near the bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100 && nextUrl) {
      isFetchingList.current = true;
      setListPayload({
        method: 'GET',
        auth: true,
        url: nextUrl
      });
    }
  }, [nextUrl, listLoading, hasMore, expandedId, errorMessage]);

  // Add scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Retry loading insights
  const handleRetry = useCallback(() => {
    setErrorMessage(null);
    isFetchingList.current = true;
    setIsInitialLoad(true);
    setListPayload({
      method: 'GET',
      auth: true,
      url: 'v0/insights/'
    });
  }, []);

  // Retry loading detail for expanded insight
  const handleRetryDetail = useCallback((id) => {
    setDetailPayload({
      method: 'GET',
      auth: true,
      url: `v0/insights/${id}`
    });
    // Set loading state
    setLoadingDetails((prev) => ({
      ...prev,
      [id]: true
    }));
  }, []);

  // WHEN USER CLICKS EXPAND
  const handleExpand = (id) => {
    if (expandedId === id) {
      dispatch(setExpandedId(null));
      return;
    }

    console.log('Expanding insight:', id);

    // Clear previous expanded data immediately
    setExpandedDetails((prev) => {
      const newDetails = { ...prev };
      delete newDetails[expandedId]; // Clear old data
      return newDetails;
    });

    // Set loading state for the new insight
    setLoadingDetails((prev) => ({
      ...prev,
      [id]: true
    }));

    dispatch(setExpandedId(id));

    // MARK AS VIEWED (POST) – only if API says it's NOT already viewed
    const insight = insights.find((item) => item.id === id);

    console.log('Insight is_already_viewed:', insight?.is_already_viewed);

    if (insight && !insight.is_already_viewed) {
      console.log('Calling mark-as-viewed for:', id);
      setViewPayload({
        method: 'POST',
        auth: true,
        url: 'v0/insights/mark-as-viewed/',
        data: {
          insight_id: id
        }
      });
    } else {
      console.log('Skipping mark-as-viewed - API says already viewed:', id);
    }

    // Always fetch fresh detail data when expanding
    setDetailPayload({
      method: 'GET',
      auth: true,
      url: `v0/insights/${id}`
    });
  };

  // Don't render if panel is not open
  if (!openAiSlider?.ai_screen) {
    return null;
  }

  return (
    <>
      <div className="slider-container">
        <div className="slider-wrapper wide-slider">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold text-xl">DAZOQ Insights</h2>
            <img
              src={betaTag}
              alt="beta"
              title="This content is generated by AI and may be inaccurate or incomplete. Users should independently verify the information in accordance with EU AI transparency requirements."
              style={{
                width: '40px'
              }}
            />
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="text-gray-500 hover:text-black cursor-pointer">
              <i className="sidebar-slider_close material-icons notranslate graphoptionicons">
                cancel
              </i>
            </div>
          </div>

          <div
            ref={containerRef}
            className="p-3 space-y-3 overflow-y-auto h-full"
            style={{ maxHeight: 'calc(100vh - 60px)' }}>
            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error loading insights</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{errorMessage}</p>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleRetry}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator for initial load */}
            {isInitialLoad && listLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
                <p className="text-gray-500 mt-2">Loading insights...</p>
              </div>
            )}

            {/* Insights List */}
            {!errorMessage &&
              insights?.map((item) => (
                <InsightItem
                  key={item.id}
                  insight={item}
                  expandedData={expandedDetails[item.id]}
                  isExpanded={expandedId === item.id}
                  isRead={item.is_already_viewed}
                  onClick={() => handleExpand(item.id)}
                  isLoading={loadingDetails[item.id] || (expandedId === item.id && detailLoading)}
                  onRetry={() => handleRetryDetail(item.id)}
                />
              ))}

            {/* Loading indicator for pagination */}
            {listLoading && !isInitialLoad && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="text-gray-500 mt-2">Loading more insights...</p>
              </div>
            )}

            {/* No more data indicator */}
            {!hasMore && insights.length > 0 && !errorMessage && (
              <div className="text-center py-4 text-gray-500">
                You&apos;ve reached the end of insights
              </div>
            )}

            {/* No insights message */}
            {!listLoading && !errorMessage && insights.length === 0 && (
              <div className="text-center py-8 text-gray-500">No insights available</div>
            )}
          </div>
        </div>

        {previewImage && (
          <div className="thumbnail-slider">
            <ThumbnailPreview />
          </div>
        )}
      </div>
    </>
  );
}
